// src/pages/public/Services.jsx
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import {
    TrendingUp,
    Zap,
    Shield,
    Clock,
    DollarSign,
    ArrowRight,
    Star,
    Check,
    Crown,
    Rocket,
    Building,
    Target,
    Globe,
    Users
} from 'lucide-react';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import PackageCard from '../../components/PackageCard';
import InvestmentCalculator from '../../components/InvestmentCalculator';

const Services = () => {
    const [selectedCategory, setSelectedCategory] = useState('all')
    const [showCalculator, setShowCalculator] = useState(false)
    const location = useLocation()

    const { data: services, isLoading, error, refetch } = useQuery({
        queryKey: ['public-services'],
        queryFn: async () => {
            try {
                const response = await api.get(endpoints.public.services)
                console.log('Fetched services response:', response.data.packages) 
                return response.data.packages || []
            } catch (error) {
                console.error('Failed to fetch services:', error)
                return []
            }
        },
        staleTime: 5 * 60 * 1000,
    })

    console.log('Services data (packages array):', services)

    const categories = [
        { 
            id: 'all', 
            name: 'All Tiers', 
            icon: Building, 
            count: Array.isArray(services) ? services.length : 0 
        },
        { 
            id: 'Starter', 
            name: 'Foundation', 
            icon: Star, 
            count: Array.isArray(services) ? services.filter(p => 
                String(p.level).toUpperCase() === 'STARTER'
            ).length : 0 
        },
        { 
            id: 'Level 2', 
            name: 'Growth', 
            icon: TrendingUp, 
            count: Array.isArray(services) ? services.filter(p => 
                String(p.level).toUpperCase() === 'LEVEL 2'
            ).length : 0 
        },
        { 
            id: 'Level 3', 
            name: 'Professional', 
            icon: Users, 
            count: Array.isArray(services) ? services.filter(p => 
                String(p.level).toUpperCase() === 'LEVEL 3'
            ).length : 0 
        },
        { 
            id: 'Level 4', 
            name: 'Premium', 
            icon: Crown, 
            count: Array.isArray(services) ? services.filter(p => 
                String(p.level).toUpperCase() === 'LEVEL 4'
            ).length : 0 
        },
        { 
            id: 'Level 5', 
            name: 'Elite', 
            icon: Rocket, 
            count: Array.isArray(services) ? services.filter(p => 
                String(p.level).toUpperCase() === 'LEVEL 5'
            ).length : 0 
        }
    ]

    const filteredPackages = Array.isArray(services) ? services.filter(pkg => {
        if (selectedCategory === 'all') return true
        return String(pkg.level).toUpperCase() === String(selectedCategory).toUpperCase()
    }) : []

    const features = [
        {
            icon: Shield,
            title: "Institutional Security",
            description: "Military-grade encryption and comprehensive insurance protection",
            color: "from-[#9E1C60] to-[#811844]"
        },
        {
            icon: Zap,
            title: "Instant Processing",
            description: "Lightning-fast deposits and withdrawals with real-time execution",
            color: "from-[#F5AD18] to-[#9E1C60]"
        },
        {
            icon: TrendingUp,
            title: "Premium Returns",
            description: "Industry-leading ROI with daily compounding growth",
            color: "from-[#811844] to-[#561530]"
        },
        {
            icon: Globe,
            title: "Global Diversification",
            description: "Internationally diversified portfolios for optimal risk management",
            color: "from-[#561530] to-[#9E1C60]"
        }
    ]

    useEffect(() => {
        refetch()
    }, [location.pathname, refetch])

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 pt-20">
            {/* Hero Section */}
            <section className="py-20 bg-gradient-to-br from-[#561530] via-[#811844] to-[#9E1C60] text-white">
                <div className="container mx-auto px-4 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-4xl mx-auto"
                    >
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                            Investment{" "}
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F5AD18] to-[#9E1C60]">
                                Tiers
                            </span>
                        </h1>
                        <p className="text-xl text-[#F5AD18] mb-8 leading-relaxed">
                            Strategic investment packages designed for optimal growth, security, 
                            and long-term wealth creation with TGFuture.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <Link
                                to="https://workplace.tgfuture.com/register"
                                className="bg-[#F5AD18] hover:bg-[#F5AD18]/90 text-[#561530] px-8 py-4 rounded-lg font-semibold text-lg inline-flex items-center gap-3 transition-all transform hover:scale-105 shadow-lg"
                            >
                                <TrendingUp className="w-6 h-6" />
                                Start Investing
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                            {/* <button
                                onClick={() => setShowCalculator(true)}
                                className="border-2 border-white text-white hover:bg-white hover:text-[#561530] px-8 py-4 rounded-lg font-semibold text-lg inline-flex items-center gap-3 transition-all"
                            >
                                <DollarSign className="w-6 h-6" />
                                Calculate Returns
                            </button> */}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features Banner */}
            <section className="py-16 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="text-center group"
                            >
                                <div className={`bg-gradient-to-r ${feature.color} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                                    <feature.icon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                                    {feature.title}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                                    {feature.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Packages Section */}
            <section className="py-20 bg-white dark:bg-gray-900">
                <div className="container mx-auto px-4">
                    {/* Section Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                            Choose Your Investment Tier
                        </h2>
                        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                            Select the investment level that aligns with your financial goals and risk tolerance
                        </p>
                    </motion.div>

                    {/* Category Filter */}
                    {/* <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-wrap justify-center gap-4 mb-16"
                    >
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => setSelectedCategory(category.id)}
                                className={`flex items-center gap-3 px-6 py-3 rounded-lg font-semibold transition-all border-2 ${
                                    selectedCategory === category.id
                                        ? 'bg-[#561530] border-[#561530] text-white shadow-lg'
                                        : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-[#9E1C60] dark:hover:border-[#F5AD18]'
                                }`}
                            >
                                <category.icon className="w-5 h-5" />
                                {category.name}
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                    selectedCategory === category.id
                                        ? 'bg-white/20 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                }`}>
                                    {category.count}
                                </span>
                            </button>
                        ))}
                    </motion.div> */}

                    {/* Packages Grid */}
                    {isLoading ? (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[...Array(6)].map((_, index) => (
                                <div key={index} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 animate-pulse">
                                    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <motion.div
                            key={selectedCategory}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                        >
                            {filteredPackages?.map((pkg, index) => (
                                <PackageCard key={pkg.id} pkg={pkg} index={index} />
                            ))}
                        </motion.div>
                    )}

                    {/* Empty State */}
                    {!isLoading && filteredPackages?.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center py-16"
                        >
                            <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Building className="w-12 h-12 text-gray-400" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                                No investment tiers found
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                                Try selecting a different category or contact our advisors for personalized investment options.
                            </p>
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 bg-[#9E1C60] hover:bg-[#811844] text-white px-6 py-3 rounded-lg font-semibold transition-all"
                            >
                                <Users className="w-5 h-5" />
                                Contact Advisors
                            </Link>
                        </motion.div>
                    )}
                </div>
            </section>

            {/* Investment Calculator Modal */}
            {showCalculator && (
                <InvestmentCalculator
                    packages={services}
                    onClose={() => setShowCalculator(false)}
                />
            )}

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-br from-[#561530] to-[#811844] text-white">
                <div className="container mx-auto px-4 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="max-w-3xl mx-auto"
                    >
                        <h2 className="text-4xl font-bold mb-6">
                            Ready to Build Your Future?
                        </h2>
                        <p className="text-xl mb-8 text-[#F5AD18]">
                            Join our community of sophisticated investors and start your journey to financial freedom
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <Link
                                to="https://workplace.tgfuture.com/register"
                                className="bg-[#F5AD18] hover:bg-[#F5AD18]/90 text-[#561530] px-8 py-4 rounded-lg font-semibold text-lg inline-flex items-center gap-3 transition-all transform hover:scale-105 shadow-lg"
                            >
                                <Rocket className="w-6 h-6" />
                                Start Your Journey
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                            <Link
                                to="/contact"
                                className="border-2 border-white text-white hover:bg-white hover:text-[#561530] px-8 py-4 rounded-lg font-semibold text-lg inline-flex items-center gap-3 transition-all"
                            >
                                <Users className="w-6 h-6" />
                                Speak to Advisor
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    )
}

export default Services